import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { PageDrawer } from 'common/components/PageDrawer';
import {
    IOfferEventListItemFragment,
    IOfferEventType,
    OfferEventsDocument,
    CreateOfferEventDocument,
} from 'graphql/types';
import { EOfferEventCategoryType, TOfferFormValues } from 'offer/types';
import { ApolloError } from 'common/components/ApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { goToOfferDetail } from 'offer/utils/goToRoutes';
import { PageTitle } from 'common/components/PageTitle';
import { OfferEventForm } from 'offer/forms/OfferEventForm';

interface IProps {
    offerEventType: IOfferEventType;
    open?: boolean;
    onClose?(): void;
}

export const CreateOfferEventDrawer = ({
    offerEventType,
    open,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const navigate = useNavigate();
    const { id: offerId } = useParams();

    const [createOfferEvent, { loading, error }] = useMutation(
        CreateOfferEventDocument
    );

    const handleSubmit = async (values: TOfferFormValues) => {
        if (!offerId) return;

        const {
            minSubscriptions,
            maxSubscriptions,
            type: offerEventType,
            offerEventTitle,
            certificate,
            ...otherValues
        } = values.event;
        const offerEventValues = {
            ...otherValues,
            minSubscriptions: minSubscriptions ? +minSubscriptions : undefined,
            maxSubscriptions: maxSubscriptions ? +maxSubscriptions : undefined,
            type: offerEventType,
            title: offerEventTitle,
            certificateId: certificate?.id,
        };

        await createOfferEvent({
            variables: {
                offerId: offerId,
                offerEvent: offerEventValues,
            },
            update: (cache, mutationResult) => {
                const { data: updatedDevelopmentItemData } = mutationResult;

                const offerEvent =
                    updatedDevelopmentItemData?.createOfferEvent?.offerEvent;

                if (!offerEvent) return;

                const offerEventsData: {
                    offerEvents: IOfferEventListItemFragment[] | null;
                } | null = cache.readQuery({
                    query: OfferEventsDocument,
                    variables: {
                        offerId: offerEvent.offer.id,
                    },
                });

                const { offerEvents } = offerEventsData || {};

                if (!offerEvents) return;

                cache.writeQuery({
                    query: OfferEventsDocument,
                    variables: {
                        offerId: offerEvent.offer.id,
                    },
                    data: {
                        offerEvents: [...offerEvents, offerEvent],
                    },
                });

                cache.evict({ fieldName: 'offerEvents' });
                cache.evict({ fieldName: 'offers' });
            },
        });

        displaySnackbar(translate('offerEventActionSuccess.create'), {
            variant: 'success',
        });

        onClose?.();
        goToOfferDetail(navigate, offerId);

        return;
    };

    const isMeetingCreatePage =
        offerEventType === IOfferEventType.Meeting ||
        offerEventType === IOfferEventType.Webinar;

    return (
        <PageDrawer
            closeOnBackdropClick={false}
            disableClose={loading}
            open={open}
            onClose={loading ? undefined : onClose}
        >
            <PageTitle>{`${translate('offers')} - ${translate(
                'offer.newEvent'
            )}`}</PageTitle>

            {error && <ApolloError error={error} />}

            <OfferEventForm
                disabled={loading}
                offerEventCategoryType={
                    isMeetingCreatePage
                        ? EOfferEventCategoryType.Meeting
                        : EOfferEventCategoryType.Offer
                }
                offerEventType={offerEventType}
                onClose={onClose}
                onSubmit={handleSubmit}
            />
        </PageDrawer>
    );
};
